<template>
  <!-- Root wrapper; fieldClass toggles the readonly/centered/bordered/gray/bottom-border-focus visual variants -->
  <div :class="fieldClass" class="new-base-textarea">
    <!-- Label, only rendered when a `label` prop is supplied -->
    <label v-if="label" class="new-base-textarea__label">
      {{ label }}
      <!-- Required-field marker, shown only when isRequired is true -->
      <span v-if="isRequired" class="new-base-textarea_required">*</span>
    </label>
    <div class="new-base-textarea__control">
      <!-- Controlled textarea: `:value` displays props.value, and every input fires newUpdateValue to emit update:value -->
      <textarea
        :value="value"
        class="new-base-textarea__input"
        :placeholder="placeholder"
        :readonly="isReadonly"
        :name="name"
        :type="type"
        :maxlength="maxlength"
        @input="newUpdateValue"
      />
    </div>
    <!-- Animates error messages in/out as they are added/removed -->
    <TransitionGroup>
      <!-- v-for renders one line per normalized validation error in `normalizedErrors` -->
      <div
        class="new-base-textarea__error"
  v-for="(item, idx) of normalizedErrors"
  :key="idx"
      >
  <span class="new-base-textarea__error-message">{{ String(item.$message) }}</span>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import {computed} from "vue";

/**
 * @emits update:value - Fired with the textarea's new string value on every native `input` event.
 */
const emit = defineEmits(["update:value"]);
/**
 * @prop {String} value - Current textarea content (controlled via `:value` + `update:value`, not native v-model).
 * @prop {String} text - Unused/reserved prop (not referenced elsewhere in this component).
 * @prop {String} label - Optional label rendered above the textarea; hidden entirely when empty.
 * @prop {String} placeholder - Native placeholder text.
 * @prop {String} type - Passed through to the native `type` attribute (has no real effect on a `<textarea>`).
 * @prop {String} name - Native `name` attribute.
 * @prop {Number} maxlength - Native `maxlength` attribute, defaults to 160.
 * @prop {Array} error - List of validation error objects (e.g. from vuelidate) rendered below the field.
 * @prop {Boolean} isReadonly - Makes the textarea read-only and applies the readonly visual style.
 * @prop {Boolean} isTextCenter - Centers the textarea's text and narrows the field.
 * @prop {Boolean} isGray - Applies the gray background variant.
 * @prop {Boolean} isRequired - Shows a red "*" next to the label.
 * @prop {Boolean} isBorder - Applies the bordered variant.
 * @prop {Boolean} isBottomBorderOnFocus - Switches to the underline-style variant (border only on focus, at the bottom).
 */
const props = defineProps({
  value: {
    type: String,
    default: "",
  },
  text: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  name: {
    type: String,
    default: "",
  },
  maxlength: {
    type: Number,
    default: 160,
  },
  error: {
    type: Array,
    required: false,
    default: () => [],
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  isTextCenter: {
    type: Boolean,
    default: false,
  },
  isGray: {
    type: Boolean,
    default: false,
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  isBorder: {
    type: Boolean,
    default: false,
  },
  isBottomBorderOnFocus: {
    type: Boolean,
    default: false,
  }
});

/** Computes the wrapper's variant classes (readonly, centered, bordered, gray, bottom-border-focus) from props. */
const fieldClass = computed(() => {
  return [
    {
      "new-base-textarea_is-readonly": props.isReadonly,
      "new-base-textarea_is-center": props.isTextCenter,
      "new-base-textarea_is-border": props.isBorder,
      "new-base-textarea_is-gray": props.isGray,
      "new-base-textarea_is-bottom-border-focus": props.isBottomBorderOnFocus,
    },
  ];
});

/** Native `input` handler: reads the textarea's current value and emits it via `update:value`. */
const newUpdateValue = (e) => {
  const t = e?.target;
  const input = t && 'value' in t ? /** @type {HTMLTextAreaElement} */ (t) : null;
  emit("update:value", input?.value ?? "");
};

/** Normalizes `props.error` (which may hold raw strings/objects) into `{ $uid, $message }` entries for consistent rendering. */
const normalizedErrors = computed(() => {
  const list = Array.isArray(props.error) ? props.error : [];
  return list.map((e, i) => {
    const obj = /** @type {any} */ (e);
    return {
      $uid: obj?.$uid ?? i,
      $message: obj?.$message ?? obj?.message ?? String(obj ?? ''),
    };
  });
});
</script>

<style lang="scss" scoped>
.new-base-textarea {
  position: relative;
  font-size: 20px;
  margin-bottom: 10px;
  &__label {
    // color: #A1A1AA;
    color: var(--color-accent-primary);
    min-height: 30px;
    margin-bottom: 0;
    font-size: 14px !important;
    font-weight: 700 !important;
  }
  &_required {
    /* OLD: color: #ff0000; */
    color: var(--color-form-input-border-error);
  }
  &__control {
    position: relative;
    display: flex;
    width: 100%;
    height: 164px;
    padding: 12px 14px;
    line-height: 145%;
    // border: 1px solid rgba(23, 23, 25, 0.1);
    // border-radius: 12px;
    border-radius: 5.285px;
    border: 1px solid var(--color-new-input-field-border) !important;
    background: var(--color-input-field-bg) !important;
    transition: 0.2s ease-in-out;
    &:focus-within {
      /* OLD: border-color: #A1A1AA; */
      border-color: var(--color-grey-quinary);
    }
  }
  &__input {
    position: relative;
    width: 100%;
    border: none;
    background: transparent;
    /* OLD: color: #9696a2; */
    color: var(--color-grey-quinary);
    font-size: 16px;
    resize: none;
    &::placeholder {
      /* OLD: color: #52525B; */
      color: var(--color-grey-octonary);
      font-size: 16px;
    }
  }
  &__error {
    margin-top: 2px;
    border-radius: 10px;
    font-size: 12px;
    /* OLD: color: #F87171; */
    color: var(--color-error-text);
    padding: 2px 10px;
  }
  &_is-readonly {
    .new-base-textarea__control {
      background: rgba(255, 255, 255, 0.05);
    }
  }
  &_is-border {
    .new-base-textarea__control{
      border: 1px solid rgba(255, 255, 255, 0.2);
      &:focus-within {
        /* OLD: border-color: #A1A1AA; */
        border-color: var(--color-grey-quinary);
      }
    }
  }
  &_is-bottom-border-focus {
    gap: 8px;
    display: flex;
    flex-direction: column;
    .new-base-textarea__label {
      font-size: 12px;
      line-height: 16px;
      min-height: auto;
    }
    .new-base-textarea__control {
      background: var(--color-background-secondary);
      border: none;
      border-bottom: 1px solid transparent;
      transition: border-bottom 0.3s ease;
    }
    .new-base-textarea__control:focus-within {
      border: none;
      border-bottom: 1px solid var(--color-foreground-primary);
    }
    .new-base-textarea__input {
      font-size: 16px;
      line-height: 20px;
      color: var(--color-foreground-primary);
    }
    .new-base-textarea__input::placeholder {
      font-size: 16px;
      line-height: 20px;
      /* OLD: color: #9696a2; */
      color: var(--color-grey-quinary);
    }
    .new-base-textarea__error {
      /* OLD: color: #F87171; */
      color: var(--color-error-text);
      padding: 0;
    }
    .new-base-textarea__control:has(+ .new-base-textarea__error) {
      border: none;
      /* OLD: border-bottom: 1px solid #DC2626; */
      border-bottom: 1px solid var(--color-error-text);
    }
  }
  &_is-gray {
    .new-base-textarea__control {
      background: var(--color-background-secondary);
    }
  }
  &_is-center &__input {
    text-align: center;
  }
  &_is-center {
    min-width: 100px;
  }
  @include _380 {
    font-size: 14px;
    &__control {
      font-size: 14px;
    }
  }
}
</style>
